import React, { useState, useMemo, useContext } from 'react';

import Axios from 'axios';
import _ from 'lodash';

import styled from 'styled-components';

import { openDocument } from '@libs/NwPrivateFile';
import { TAG_FILTER_MODE_DEFAULT, matchesTagFilter, sliceData } from '@libs/NwUtils';

import { useStateValue } from '@contexts/StateProvider';
import { UserContext, useUserPermission } from '@contexts/UserContext';

//import { useContact } from "@hooks/Contact/UseContact";
import { onContactMediaUpdate } from '@hooks/API/Contact/UseContactMedia';
import { useAdjustTagUsageCount } from '@hooks/CachedLists/UseTagCatalog';
import useNwBreakPoints from '@hooks/Layout/UseNwBreakPoints';

import CreatedBy from '@components/CreatedBy/CreatedBy';
import CustomSwitch from '@components/Gui/CustomSwitch';
import EmptyState from '@components/Gui/EmptyState';
import FlexContainer from '@components/Gui/FlexContainer';
import { Loading } from '@components/Gui/Loading';
import { NwCard, NwCardContent } from '@components/Gui/NwCard/NwCard';
import { NwCardTabsContent } from '@components/Gui/NwCard/NwCardTabs';
import NwDropDownMenu from '@components/Gui/NwDropDownMenu';
import NwIcon from '@components/Gui/NwIcon';
// import NwObjectCounter from '@components/Gui/NwObjectCounter';
import { NWList, NWListItem } from '@components/Gui/NWList';
import NwUpload from '@components/Gui/NwUpload';
import MediaDrawer from '@components/MediaDrawer/MediaDrawer';
import TagFilterPicker from '@components/TagPicker/TagFilterPicker';

import Document from './Document';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    height: 100%;
    position: relative;
    .ant-card-body-content {
        padding: 0 !important;
        justify-content: ${(props) => !props.viewModelNotes && 'center !important'};
    }
    .ant-list-item-meta-title {
        margin-bottom: 0;
    }

    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: flex-start;
    .tabs-content {
        justify-content: ${(props) => !props.viewModelNotes && 'center'};
    }
`;

const ActionButtonsDropdown = styled.div`
    text-align: right;
`;

const Description = styled.div`
    font-size: 0.95rem;
`;

const DocAuthorText = styled.div`
    font-size: 0.6rem;
    text-transform: uppercase;
    margin-top: 0.3em;
`;

const LockContainer = styled(FlexContainer)`
    height: 100%;
    padding: 32px 0;
    svg {
        font-size: ${(props) => (props.$readOnly ? '80px' : '120px')};
    }
`;

const ContactDetailsDocuments = ({ currentContact, title, disableRating, isInCardTab, readOnly }) => {
    const itemsPerPage = 4;
    const [activePage, setActivePage] = useState(1);
    const [{ areaContext }] = useStateValue();
    const { currentUser } = useContext(UserContext);
    const [documentId, setDocumentId] = useState(null);
    const [isDocumentDrawer, setIsDocumentDrawer] = useState(false);
    const [isShowComment, setIsShowComment] = useState(false);
    const [isReloading, setIsReloading] = useState(false);
    const [filterTags, setTags] = useState([]);
    const [tagFilterMode, setTagFilterMode] = useState(TAG_FILTER_MODE_DEFAULT);
    const [onlyMine, setOnlyMine] = useState(false);
    const breakpoints = useNwBreakPoints();
    const adjustTagUsageCount = useAdjustTagUsageCount();
    //const { data: currentContact } = useContact(contactID);

    const viewClientFiles = useUserPermission(
        'ViewClientFiles',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const viewAgencyFiles = useUserPermission(
        'ViewAgencyFiles',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const viewServiceFiles = useUserPermission(
        'ViewServiceFiles',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const viewModelFiles = useUserPermission(
        'ViewModelFiles',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );

    const viewFiles = useMemo(() => {
        if (currentContact) {
            if (currentContact.SubType === 1) return viewClientFiles;
            if (currentContact.SubType === 2) return viewAgencyFiles;
            if (currentContact.SubType === 3) return viewServiceFiles;
            return viewModelFiles;
        }
        return false;
    }, [currentContact, viewClientFiles, viewAgencyFiles, viewServiceFiles, viewModelFiles]);

    const handlePageChange = (page) => {
        setActivePage(page);
    };

    const sortedDocuments = useMemo(() => {
        let documentList = currentContact
            ? currentContact.Files.filter((document) => {
                  if (
                      document.Visibility.Areas.length === 0 ||
                      document.Visibility.Areas.find((area) => area === areaContext.area)
                  ) {
                      return true;
                  }
                  return false;
              })
            : [];

        documentList = documentList.filter((document) => matchesTagFilter(document, filterTags, tagFilterMode));

        documentList = documentList.filter((book) => !onlyMine || book.UserCreated === currentUser.UserID);

        _.sortBy(documentList, 'ID').reverse();
        return documentList;

    }, [currentContact, filterTags, tagFilterMode, onlyMine, currentUser]);

    const documentsToRender =
        breakpoints === 1 ? sortedDocuments : sliceData(activePage, itemsPerPage, sortedDocuments);

    const handleDocumentEdit = (document) => {
        setDocumentId(document.ID);
        setIsDocumentDrawer(true);
    };

    const document = sortedDocuments && sortedDocuments.find((item) => item.ID === documentId);

    const RenderUploadButton = () => (
        <NwUpload
            maxSize={20}
            showList={false}
            disable={!viewFiles}
            types={['document', 'image', 'data-document', 'office-document', 'archive-document', 'text-document']}
            onUploadStart={() => {}}
            onUploadEnd={() => {
                setIsReloading(true);
                onContactMediaUpdate(currentContact.ID, () => setIsReloading(false));
            }}
            endpoint={`documents/uploadsingle/1?contactID=${currentContact.ID}`}
        />
    );

    const onCloseDrawer = () => {
        setIsDocumentDrawer(false);
        setIsShowComment(false);
    };

    const selectMediaBefore = () => {
        const findIndex = sortedDocuments.findIndex((document) => document.ID === documentId);
        if (findIndex > 0) {
            setDocumentId(sortedDocuments[findIndex - 1].ID);
        }
    };

    const selectMediaAfter = () => {
        const findIndex = sortedDocuments.findIndex((document) => document.ID === documentId);
        if (findIndex > -1 && findIndex < sortedDocuments.length - 1) {
            setDocumentId(sortedDocuments[findIndex + 1].ID);
        }
    };

    const OnSelectCover = () => {};

    const handleDocumentDelete = (document) => {
        Axios.delete(`/documents/?ids=${document.ID}`)
            .then(() => {
                // il documento eliminato non usa piu' i suoi tag
                (document.Tags || []).forEach((tag) => adjustTagUsageCount(tag.ID, -1));
                if (onContactMediaUpdate) {
                    onContactMediaUpdate(currentContact.ID);
                }
            })
            .catch((error) => {
                console.error('Errore', error);
            });
    };

    const handleDocumentDownload = (document) => {
        openDocument(document, { download: true });
    };

    const getDescription = (document) => {
        // const onOpenDocumentComments = () => {
        //     setDocumentId(document.ID);
        //     setIsShowComment(true);
        //     setIsDocumentDrawer(true);
        // };

        return (
            <Description>
                <DocAuthorText>
                    <CreatedBy object={document} short textonly />
                </DocAuthorText>
                {/* <NwObjectCounter count={document.Comments.length} label="comments" onClick={onOpenDocumentComments} /> */}
            </Description>
        );
    };

    const actions = (document) => {
        return (
            <ActionButtonsDropdown>
                <NwDropDownMenu
                    actions={[
                        {
                            name: '',
                            key: 'edit',
                            pinned: true,
                            icon: byPrefixAndName.fasl['pen'],
                            action: () => handleDocumentEdit(document),
                        },
                        {
                            name: '',
                            key: 'download',
                            pinned: true,
                            icon: byPrefixAndName.fasl['cloud-download'],
                            action: () => handleDocumentDownload(document),
                        },
                        {
                            name: '',
                            type: 'delete',
                            key: 'delete',
                            pinned: true,
                            icon: byPrefixAndName.fasl['trash-alt'],
                            confirmationPlacement: 'left',
                            action: () => handleDocumentDelete(document),
                        },
                    ]}
                />
            </ActionButtonsDropdown>
        );
    };

    const cardContent = () =>
        viewFiles ? (
            <>
                {documentsToRender && documentsToRender.length > 0 ? (
                    <NwCardContent
                        pagination={
                            breakpoints === 1
                                ? null
                                : {
                                      total: sortedDocuments.length,
                                      current: activePage,
                                      pageSize: itemsPerPage,
                                      onChange: handlePageChange,
                                  }
                        }
                    >
                        {isReloading ? (
                            <Loading />
                        ) : (
                            <NWList>
                                {documentsToRender.map((document) => (
                                    <NWListItem
                                        actions={!readOnly ? actions(document) : null}
                                        key={document.ID}
                                        renderContent={() => (
                                            <Document
                                                document={document}
                                                getDescription={getDescription}
                                                actions={!readOnly && actions}
                                                onDocumentClick={handleDocumentEdit}
                                            />
                                        )}
                                    />
                                ))}
                            </NWList>
                        )}
                    </NwCardContent>
                ) : (
                    <EmptyState message="no documents" />
                )}
            </>
        ) : (
            <LockContainer $readOnly={readOnly} justifyContent="center" alignItems="center">
                <NwIcon icon={byPrefixAndName.fasl['lock-alt']} />
            </LockContainer>
        );

    const handleSetTags = (name, tags) => {
        setTags(tags);
    };

    const renderFilters = () => {
        return breakpoints === 1 ? (
            <FlexContainer fullWidth column justifyContent="space-between">
                <FlexContainer mg="0 0 8px">
                    <TagFilterPicker
                        itemType="Document"
                        selectedOptions={filterTags}
                        filterMode={tagFilterMode}
                        onChange={handleSetTags}
                        onFilterModeChange={setTagFilterMode}
                    />
                </FlexContainer>
                <CustomSwitch checked={onlyMine} onChange={setOnlyMine} text="only mine" />
            </FlexContainer>
        ) : (
            <FlexContainer fullWidth justifyContent="space-between">
                <TagFilterPicker
                    itemType="Document"
                    selectedOptions={filterTags}
                    filterMode={tagFilterMode}
                    onChange={handleSetTags}
                    onFilterModeChange={setTagFilterMode}
                />
                <CustomSwitch checked={onlyMine} onChange={setOnlyMine} text="only mine" />
            </FlexContainer>
        );
    };

    const getActiveFilters = () => {
        let filters = [];
        if (filterTags.length) {
            for (const tag of filterTags) {
                filters.push(tag);
            }
        }
        if (onlyMine) {
            filters.push('Mine only');
        }
        return filters;
    };

    if (!currentContact) {
        return <div />;
    }

    if (isInCardTab) {
        return (
            <Wrapper>
                <NwCardTabsContent
                    filtersContent={viewFiles && renderFilters()}
                    activeFilters={viewFiles && getActiveFilters()}
                >
                    {cardContent()}
                </NwCardTabsContent>
                {isDocumentDrawer && document && (
                    <MediaDrawer
                        mediaID={document.ID}
                        disableRating={disableRating}
                        contactID={currentContact.ID}
                        isShowComment={isShowComment}
                        onCloseDrawer={onCloseDrawer}
                        onSelectMediaBefore={selectMediaBefore}
                        onSelectMediaAfter={selectMediaAfter}
                        onMediaUpdate={onContactMediaUpdate}
                        onSelectCover={OnSelectCover}
                        afterNewMediaCreation={onCloseDrawer}
                    />
                )}
            </Wrapper>
        );
    } else {
        return (
            <Wrapper>
                <NwCard
                    title={title || 'Files'}
                    icon={byPrefixAndName.fasl['file']}
                    fullheight
                    padding="reduced"
                    filtersContent={viewFiles && renderFilters()}
                    activeFilters={viewFiles && getActiveFilters()}
                    extra={RenderUploadButton()}
                >
                    {cardContent()}
                </NwCard>
                {isDocumentDrawer && document && (
                    <MediaDrawer
                        mediaID={document.ID}
                        disableRating={disableRating}
                        contactID={currentContact.ID}
                        isShowComment={isShowComment}
                        onCloseDrawer={onCloseDrawer}
                        onSelectMediaBefore={selectMediaBefore}
                        onSelectMediaAfter={selectMediaAfter}
                        onMediaUpdate={onContactMediaUpdate}
                        onSelectCover={OnSelectCover}
                        afterNewMediaCreation={onCloseDrawer}
                    />
                )}
            </Wrapper>
        );
    }
};

export default ContactDetailsDocuments;
